import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pprApi, type PprJob } from '@/lib/apiClient';
import { fmtPct, fmtDate } from '../pprShared';

/**
 * Financial Data tab.
 *
 * All 21 financial fields, grouped per the schema. Edits track locally;
 * Save submits only the dirty fields to pprApi.updateJob.
 *
 * Margin percent fields are stored as decimal (0.1234 = 12.34%) but the
 * UI lets the coordinator type percentages (12.34). The conversion happens
 * on read (load → input) and write (input → save).
 *
 * Below each $-margin field we show a live "implied %" computed from the
 * margin and contract value — useful as a sanity check against the
 * separately-entered margin-pct fields.
 *
 * "Mark Financials Complete" stamps financialsCompleteDate and bumps the
 * job to Awaiting Financials → Report Prep via the dedicated endpoint
 * (backend already handles the status transition).
 */
export default function FinancialsTab({
  job,
  canEdit,
  canComplete = true,
}: {
  job: PprJob;
  canEdit: boolean;
  /**
   * May this reader mark financials complete on THIS job? False for an
   * Accounting (`ppr.financials.edit`) reader on a job that has not reached
   * Awaiting Financials — the API scopes them to that stage, so showing the
   * button would just hand them a 400. They can still type the numbers in.
   */
  canComplete?: boolean;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => jobToForm(job));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Track only changed fields so we PATCH a minimal payload.
  const dirtyPatch = useMemo(() => diffForm(jobToForm(job), form), [job, form]);
  const dirty = Object.keys(dirtyPatch).length > 0;

  const saveMutation = useMutation({
    mutationFn: () => pprApi.updateJob(job.id, dirtyPatch),
    onSuccess: (updated) => {
      // Reset the baseline to the saved values so `dirty` clears reliably
      // (otherwise edited strings linger and the tab stays "dirty" forever,
      // which also keeps "Mark Financials Complete" grayed out).
      setForm(jobToForm(updated));
      setSaveError(null);
      setLastSavedAt(new Date());
      qc.invalidateQueries({ queryKey: ['ppr', 'job', job.id] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  const completeMutation = useMutation({
    mutationFn: () => pprApi.markFinancialsComplete(job.id),
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ['ppr', 'job', job.id] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // Convenience handler — all numeric fields use the same shape.
  const setNum = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setForm((f) => ({ ...f, [key]: raw === '' ? '' : raw }));
  };

  return (
    <>
      {job.financialsComplete && (
        <div className="ppr-fin-complete-banner">
          ✓ Financials marked complete on {fmtDate(job.financialsCompleteDate)}.
        </div>
      )}

      <Section title="Contract & Bid">
        <Field label="Contract Value">
          <input className="ppr-input" disabled={!canEdit} value={form.contractValue} onChange={setNum('contractValue')} placeholder="0.00" />
        </Field>
        <Field label="Original Bid">
          <input className="ppr-input" disabled={!canEdit} value={form.originalBid} onChange={setNum('originalBid')} placeholder="0.00" />
        </Field>
        <Field label="Total Costs">
          <input className="ppr-input" disabled={!canEdit} value={form.totalCosts} onChange={setNum('totalCosts')} placeholder="0.00" />
        </Field>
        <Field label="Total Costs (w/o Directs)">
          <input className="ppr-input" disabled={!canEdit} value={form.totalCostsWoDirects} onChange={setNum('totalCostsWoDirects')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Margins ($)">
        <Field label="Original Bid Margin" derived={impliedPct(form.originalBidMargin, form.contractValue)}>
          <input className="ppr-input" disabled={!canEdit} value={form.originalBidMargin} onChange={setNum('originalBidMargin')} placeholder="0.00" />
        </Field>
        <Field label="Final Margin" derived={impliedPct(form.finalMargin, form.contractValue)}>
          <input className="ppr-input" disabled={!canEdit} value={form.finalMargin} onChange={setNum('finalMargin')} placeholder="0.00" />
        </Field>
        <Field label="Super Margin" derived={impliedPct(form.superMargin, form.contractValue)}>
          <input className="ppr-input" disabled={!canEdit} value={form.superMargin} onChange={setNum('superMargin')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Margins (%)">
        <Field label="Original Bid Margin %" derived="Enter as percent — e.g. 12.34">
          <input className="ppr-input" disabled={!canEdit} value={form.originalBidMarginPct} onChange={setNum('originalBidMarginPct')} placeholder="12.34" />
        </Field>
        <Field label="Final Margin %" derived="Enter as percent">
          <input className="ppr-input" disabled={!canEdit} value={form.finalMarginPct} onChange={setNum('finalMarginPct')} placeholder="12.34" />
        </Field>
        <Field label="Super Margin %" derived="Enter as percent">
          <input className="ppr-input" disabled={!canEdit} value={form.superMarginPct} onChange={setNum('superMarginPct')} placeholder="12.34" />
        </Field>
      </Section>

      <Section title="Equipment">
        <Field label="Equipment Bid">
          <input className="ppr-input" disabled={!canEdit} value={form.equipmentBid} onChange={setNum('equipmentBid')} placeholder="0.00" />
        </Field>
        <Field label="Equipment Costs">
          <input className="ppr-input" disabled={!canEdit} value={form.equipmentCosts} onChange={setNum('equipmentCosts')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Materials">
        <Field label="Material Bid">
          <input className="ppr-input" disabled={!canEdit} value={form.materialBid} onChange={setNum('materialBid')} placeholder="0.00" />
        </Field>
        <Field label="Actual Material Expense">
          <input className="ppr-input" disabled={!canEdit} value={form.actualMaterialExpense} onChange={setNum('actualMaterialExpense')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Subcontract">
        <Field label="Sub Bid">
          <input className="ppr-input" disabled={!canEdit} value={form.subBid} onChange={setNum('subBid')} placeholder="0.00" />
        </Field>
        <Field label="Sub Costs">
          <input className="ppr-input" disabled={!canEdit} value={form.subCosts} onChange={setNum('subCosts')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Labor Hours">
        <Field label="Total Bid Labor Hours">
          <input className="ppr-input" disabled={!canEdit} value={form.totalBidLaborHours} onChange={setNum('totalBidLaborHours')} placeholder="0.00" />
        </Field>
        <Field label="Actual Job Hours">
          <input className="ppr-input" disabled={!canEdit} value={form.actualJobHours} onChange={setNum('actualJobHours')} placeholder="0.00" />
        </Field>
        <Field label="Straight-Time Hours">
          <input className="ppr-input" disabled={!canEdit} value={form.straightTimeHours} onChange={setNum('straightTimeHours')} placeholder="0.00" />
        </Field>
        <Field label="OT Hours">
          <input className="ppr-input" disabled={!canEdit} value={form.otHours} onChange={setNum('otHours')} placeholder="0.00" />
        </Field>
      </Section>

      <Section title="Other">
        <Field label="Per Diem">
          <input className="ppr-input" disabled={!canEdit} value={form.perDiem} onChange={setNum('perDiem')} placeholder="0.00" />
        </Field>
      </Section>

      {canEdit && (
        <div className="ppr-fin-actions">
          <button
            className="btn btn-primary"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
          </button>
          {!job.financialsComplete && canComplete && (
            <button
              className="btn"
              disabled={completeMutation.isPending || dirty}
              title={dirty ? 'Save your changes first' : ''}
              onClick={() => completeMutation.mutate()}
            >
              {completeMutation.isPending ? '…' : 'Mark Financials Complete'}
            </button>
          )}
          {lastSavedAt && !dirty && !saveError && (
            <span className="ppr-fin-saved">✓ Saved at {lastSavedAt.toLocaleTimeString()}</span>
          )}
          {saveError && <span className="ppr-fin-error">{saveError}</span>}
        </div>
      )}
    </>
  );
}

// ─── Layout primitives ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="ppr-fin-section">
      <h3>{title}</h3>
      <div className="ppr-fin-grid">{children}</div>
    </div>
  );
}

function Field({
  label,
  derived,
  children,
}: {
  label: string;
  derived?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="ppr-fin-field">
      <label>{label}</label>
      {children}
      {derived && <div className="ppr-derived">{derived}</div>}
    </div>
  );
}

// ─── Form / data conversion ────────────────────────────────────────────────────

type FormState = Record<FormKey, string>;

type FormKey =
  | 'contractValue' | 'originalBid' | 'totalCosts' | 'totalCostsWoDirects'
  | 'originalBidMargin' | 'finalMargin' | 'superMargin'
  | 'originalBidMarginPct' | 'finalMarginPct' | 'superMarginPct'
  | 'equipmentBid' | 'equipmentCosts'
  | 'materialBid' | 'actualMaterialExpense'
  | 'subBid' | 'subCosts'
  | 'totalBidLaborHours' | 'actualJobHours' | 'straightTimeHours' | 'otHours'
  | 'perDiem';

const PCT_KEYS = new Set<FormKey>(['originalBidMarginPct', 'finalMarginPct', 'superMarginPct']);

function jobToForm(job: PprJob): FormState {
  const num = (v: number | null, asPct = false) => {
    if (v === null || v === undefined) return '';
    return asPct ? (v * 100).toString() : v.toString();
  };
  return {
    contractValue:        num(job.contractValue),
    originalBid:          num(job.originalBid),
    totalCosts:           num(job.totalCosts),
    totalCostsWoDirects:  num(job.totalCostsWoDirects),
    originalBidMargin:    num(job.originalBidMargin),
    finalMargin:          num(job.finalMargin),
    superMargin:          num(job.superMargin),
    originalBidMarginPct: num(job.originalBidMarginPct, true),
    finalMarginPct:       num(job.finalMarginPct, true),
    superMarginPct:       num(job.superMarginPct, true),
    equipmentBid:         num(job.equipmentBid),
    equipmentCosts:       num(job.equipmentCosts),
    materialBid:          num(job.materialBid),
    actualMaterialExpense: num(job.actualMaterialExpense),
    subBid:               num(job.subBid),
    subCosts:             num(job.subCosts),
    totalBidLaborHours:   num(job.totalBidLaborHours),
    actualJobHours:       num(job.actualJobHours),
    straightTimeHours:    num(job.straightTimeHours),
    otHours:              num(job.otHours),
    perDiem:              num(job.perDiem),
  };
}

/**
 * Build the PATCH payload from only the fields that changed. Empty strings
 * convert to null; numeric strings parse to numbers; percent fields are
 * divided by 100 on the way out (UI shows 12.34, DB stores 0.1234).
 */
function diffForm(orig: FormState, current: FormState): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const k of Object.keys(current) as FormKey[]) {
    const c = current[k];
    const o = orig[k];
    const cEmpty = c === '' || c === null || c === undefined;
    const oEmpty = o === '' || o === null || o === undefined;
    if (cEmpty && oEmpty) continue;                 // both blank → unchanged
    const cNum = cEmpty ? null : Number(c);
    if (!cEmpty && Number.isNaN(cNum)) continue;     // invalid entry → ignore (don't mark dirty)
    const oNum = oEmpty ? null : Number(o);
    if (cNum === oNum) continue;                     // numerically equal → unchanged (ignores "1000" vs "1000.00")
    if (cEmpty) { out[k] = null; continue; }         // cleared a value
    out[k] = PCT_KEYS.has(k) ? (cNum as number) / 100 : (cNum as number);
  }
  return out;
}

/** Helper text under a $-margin field: shows the implied % vs contract value. */
function impliedPct(marginStr: string, contractValueStr: string): string {
  if (!marginStr || !contractValueStr) return ' ';
  const m = Number(marginStr);
  const cv = Number(contractValueStr);
  if (!Number.isFinite(m) || !Number.isFinite(cv) || cv === 0) return ' ';
  return `≈ ${fmtPct(m / cv)} of contract`;
}

